import type { Json } from '@ctrl/db';

import { supabase } from '../supabase.ts';

/**
 * Metrc, per licence.
 *
 * Connecting is discovery-driven: the merchant supplies only their USER key,
 * the `ctrl-metrc` worker pairs it with the platform vendor key and hands back
 * the facilities that key can reach, and they associate one of those
 * facilities with one of their locations.
 *
 * Discovery is the only step that needs a server of ours -- it spends a
 * platform secret. The connect that follows is a plain PostgREST RPC, because
 * `connect_metrc_facility` needs nothing the browser does not already have.
 *
 * The user key goes to Vault through `connect_metrc_facility` and only the
 * reference is stored on `metrc_configs` -- which is why this module can list
 * a connection and clear one, but never fetch one back. Replacing a key means
 * typing it again, which is the point.
 *
 * @see docs/metrc.md
 */

/**
 * Every state running Metrc. Must stay in step with `METRC_STATES` in
 * `apps/metrc/src/lib/metrc.ts`, which is what actually validates the choice --
 * this list only decides what the dropdown offers.
 */
export const METRC_STATES = [
  'CA',
  'AK',
  'AL',
  'AZ',
  'CO',
  'CT',
  'DC',
  'IL',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NJ',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'RI',
  'SD',
  'WV',
] as const;

export interface MetrcConfig {
  id: string;
  licenseId: string;
  state: string;
  enabled: boolean;
  sandbox: boolean;
  lastVerifiedAt: string | null;
  lastFullSyncAt: string | null;
  lastError: string | null;
  /** The connected facility record, verbatim from Metrc. `{}` before connect. */
  facility: Record<string, unknown>;
}

/** One facility as `metrc_facilities_get` shapes it. */
export interface MetrcFacility {
  licenseNumber: string;
  licenseType: string | null;
  licenseStartDate: string | null;
  licenseEndDate: string | null;
  name: string;
  displayName: string;
  alias: string | null;
  isProvisional: boolean;
  /** `FacilityType` flags that are true, e.g. `CanSellToConsumers`. */
  permissions: string[];
  /**
   * Verbatim Metrc record; passed straight back on connect, which is why it
   * is typed as `Json` rather than a record -- it goes into a jsonb column
   * whole and nothing here reads a field out of it.
   */
  raw: Json;
}

export async function listMetrcConfigs(accountId: string): Promise<MetrcConfig[]> {
  const { data, error } = await supabase
    .from('metrc_configs')
    .select(
      'id, license_id, state, enabled, sandbox, last_verified_at, last_full_sync_at, last_error, facility',
    )
    .eq('account_id', accountId);

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    licenseId: row.license_id,
    state: row.state,
    enabled: row.enabled,
    sandbox: row.sandbox,
    lastVerifiedAt: row.last_verified_at,
    lastFullSyncAt: row.last_full_sync_at,
    lastError: row.last_error,
    facility: (row.facility ?? {}) as Record<string, unknown>,
  }));
}

const METRC_URL = import.meta.env.VITE_METRC_URL;

/**
 * One request to the Metrc worker.
 *
 * The worker answers failures as `{ error, reason }` and, on a sync, includes
 * the progress the call still made before it stopped -- so a throttled or
 * failed slice is not lost work. `MetrcSyncError` carries both to the caller.
 */
async function metrcRequest<T>(path: string, init: RequestInit): Promise<T> {
  const { data: session } = await supabase.auth.getSession();
  const token = session.session?.access_token;

  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${METRC_URL}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    },
  });

  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
    reason?: string;
    data?: unknown;
  };

  if (!response.ok) {
    throw new MetrcSyncError(
      body.error ?? `Metrc request failed (${response.status}).`,
      body.reason ?? 'error',
      (body.data as never) ?? null,
    );
  }

  return body as T;
}

/**
 * Verifies a user key and lists the facilities it can reach.
 *
 * The key is sent once, used server-side, and never stored by this call --
 * nothing is written until `connectMetrcFacility`. The worker answers with a
 * sentence in `error`, so failures reach the merchant as the specific mistake
 * they made rather than a status code.
 */
export async function fetchMetrcFacilities(input: {
  userKey: string;
  state: string;
  sandbox: boolean;
  accountId: string;
}): Promise<MetrcFacility[]> {
  const body = await metrcRequest<{ data: MetrcFacility[] }>(
    `/admin/accounts/${input.accountId}/metrc/facilities`,
    {
      method: 'POST',
      body: JSON.stringify({
        userKey: input.userKey,
        state: input.state,
        sandbox: input.sandbox,
      }),
    },
  );

  return body.data ?? [];
}

/**
 * Associates one discovered facility with one location.
 *
 * A plain PostgREST RPC, not a call to the worker: `connect_metrc_facility`
 * needs no platform secret, only the caller's own permission, which the
 * function checks itself. It upserts the licence (keyed on its number, so a
 * re-connect re-points rather than duplicating), puts the user key in Vault,
 * and stores the facility record whole.
 */
export async function connectMetrcFacility(input: {
  accountId: string;
  locationId: string;
  state: string;
  sandbox: boolean;
  userKey: string;
  facility: MetrcFacility;
}): Promise<void> {
  const { error } = await supabase.rpc('connect_metrc_facility', {
    p_account_id: input.accountId,
    p_location_id: input.locationId,
    p_state: input.state,
    p_sandbox: input.sandbox,
    p_user_key: input.userKey,
    p_license_number: input.facility.licenseNumber,
    // Metrc's FacilityType is a permission object, not our licence taxonomy,
    // so the licence lands as 'retail' for the merchant to refine under
    // Locations. The facility record keeps the real detail.
    p_license_type: 'retail',
    p_facility: input.facility.raw,
  });

  if (error) throw error;
}

/* ============================================================
 * Package sync.
 * ============================================================ */

export interface MetrcPackageStatus {
  configId: string;
  licenseNumber: string;
  state: string;
  sandbox: boolean;
  enabled: boolean;
  lastFullSyncAt: string | null;
  packageCount: number;
}

/** Where a partial sync left off. Opaque to the console -- echoed straight back. */
export interface MetrcSyncCursor {
  listType: 'active' | 'onhold' | 'inactive';
  page: number;
}

export interface MetrcSyncProgress {
  imported: number;
  listType?: MetrcSyncCursor['listType'];
  page?: number;
  totalPages?: number;
  done: boolean;
  cursor: MetrcSyncCursor | null;
  window: { start: string; end: string };
  lastFullSyncAt?: string | null;
  retryAfterSeconds?: number;
}

/** Thrown by `syncMetrcPackages`, carrying the progress the failed call still made. */
export class MetrcSyncError extends Error {
  constructor(
    message: string,
    readonly reason: string,
    readonly progress: MetrcSyncProgress | null,
  ) {
    super(message);
    this.name = 'MetrcSyncError';
  }
}

/** Last sync date and mirror size, for the line above the sync button. */
export async function getMetrcPackageStatus(accountId: string): Promise<MetrcPackageStatus | null> {
  const body = await metrcRequest<{ data: MetrcPackageStatus | null }>(
    `/admin/accounts/${accountId}/metrc/packages/status`,
    { method: 'GET' },
  );

  return body.data;
}

/**
 * One slice of a package sync.
 *
 * Deliberately not the whole walk: the worker can only spend so many
 * subrequests per request, so it does a bounded number of pages and returns a
 * cursor. Callers loop until `done` -- see `runMetrcPackageSync`.
 */
export async function syncMetrcPackages(
  accountId: string,
  input: {
    configId?: string;
    cursor?: MetrcSyncCursor | null;
    window?: { start: string; end: string };
  },
): Promise<MetrcSyncProgress> {
  const body = await metrcRequest<{ data: MetrcSyncProgress }>(
    `/admin/accounts/${accountId}/metrc/packages/sync`,
    {
      method: 'POST',
      body: JSON.stringify({
        configId: input.configId,
        cursor: input.cursor ?? undefined,
        windowStart: input.window?.start,
        windowEnd: input.window?.end,
      }),
    },
  );

  return body.data;
}

/**
 * Runs a sync to completion, one slice at a time.
 *
 * The loop lives here rather than in the worker because the bound is the
 * worker's: it can only spend so many subrequests per request. `onProgress`
 * fires after each slice so the screen can count up rather than sit still for
 * what may be several minutes.
 *
 * A throttle is waited out rather than surfaced -- Metrc tells us how long, and
 * the cursor means resuming costs nothing. Every other failure stops the run
 * and keeps whatever was already committed.
 */
export async function runMetrcPackageSync(
  accountId: string,
  options: {
    configId?: string;
    onProgress?: (progress: MetrcSyncProgress, totalImported: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<{ imported: number; lastFullSyncAt: string | null }> {
  let cursor: MetrcSyncCursor | null = null;
  let window: { start: string; end: string } | undefined;
  let imported = 0;
  let lastFullSyncAt: string | null = null;

  for (;;) {
    if (options.signal?.aborted) return { imported, lastFullSyncAt };

    let slice: MetrcSyncProgress;

    try {
      slice = await syncMetrcPackages(accountId, {
        configId: options.configId,
        cursor,
        window,
      });
    } catch (error) {
      if (error instanceof MetrcSyncError && error.reason === 'rate_limited' && error.progress) {
        imported += error.progress.imported;
        cursor = error.progress.cursor;
        window = error.progress.window;

        const wait = (error.progress.retryAfterSeconds ?? 30) * 1000;

        options.onProgress?.(error.progress, imported);
        await new Promise((resolve) => setTimeout(resolve, wait));
        continue;
      }

      throw error;
    }

    imported += slice.imported;
    cursor = slice.cursor;
    window = slice.window;
    lastFullSyncAt = slice.lastFullSyncAt ?? lastFullSyncAt;

    options.onProgress?.(slice, imported);

    if (slice.done || !cursor) return { imported, lastFullSyncAt };
  }
}

/** Drops the Vault secrets and switches reporting off; the row stays as history. */
export async function clearMetrcCredentials(accountId: string, configId: string): Promise<void> {
  const { error } = await supabase.rpc('clear_metrc_credentials', {
    p_account_id: accountId,
    p_config_id: configId,
  });

  if (error) throw error;
}
